import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useDemo } from '@/contexts/DemoContext';

/**
 * useDemoDates - Hook for dates that supports demo mode
 * 
 * In demo mode, data is read from and written to local storage.
 * In real mode, data is fetched from and written to the backend API.
 */

const useDemoDates = (category = null) => {
  const { isDemoMode, getImportantDates, addImportantDate, updateImportantDate, deleteImportantDate } = useDemo();
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDates = useCallback(async () => {
    try {
      setLoading(true);
      
      // DEMO MODE: Get dates from local demo data
      if (isDemoMode) {
        const demoDates = getImportantDates();
        // Filter by category if specified
        const filtered = category 
          ? demoDates.filter(d => d.category === category)
          : demoDates;
        setDates(filtered);
        setLoading(false);
        return;
      }
      
      // REAL MODE: Fetch from API
      const url = category ? `/dates?category=${category}` : '/dates';
      const response = await API.get(url);
      setDates(response.data);
    } catch (error) {
      console.error('Failed to fetch dates:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [isDemoMode, category, getImportantDates]);

  // Fetch on mount and when isDemoMode/category changes
  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  const addDate = useCallback(async (dateData) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const newDate = addImportantDate({
          ...dateData,
          category: category || dateData.category
        });
        setDates(prev => [...prev, newDate]);
        toast.success('Event added');
        return newDate;
      }
      
      // REAL MODE
      const response = await API.post('/dates', {
        ...dateData,
        category: category || dateData.category
      });
      setDates(prev => [...prev, response.data]);
      toast.success('Event added');
      return response.data;
    } catch (error) {
      console.error('Failed to add date:', error);
      toast.error('Failed to add event');
      throw error;
    }
  }, [isDemoMode, category, addImportantDate]);

  const updateDate = useCallback(async (id, updates) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        const updated = updateImportantDate(id, updates);
        setDates(prev => prev.map(d => d.id === id ? updated : d));
        toast.success('Event updated');
        return updated;
      }
      
      // REAL MODE
      const response = await API.put(`/dates/${id}`, updates);
      setDates(prev => prev.map(d => d.id === id ? response.data : d));
      toast.success('Event updated');
      return response.data;
    } catch (error) {
      console.error('Failed to update date:', error);
      toast.error('Failed to update event');
      throw error;
    }
  }, [isDemoMode, updateImportantDate]);

  const deleteDate = useCallback(async (id) => {
    try {
      // DEMO MODE
      if (isDemoMode) {
        deleteImportantDate(id);
        setDates(prev => prev.filter(d => d.id !== id));
        toast.success('Event removed');
        return;
      }
      
      // REAL MODE
      await API.delete(`/dates/${id}`);
      setDates(prev => prev.filter(d => d.id !== id));
      toast.success('Event removed');
    } catch (error) {
      console.error('Failed to delete date:', error);
      toast.error('Failed to remove event');
      throw error;
    }
  }, [isDemoMode, deleteImportantDate]);

  return {
    dates,
    loading,
    fetchDates,
    addDate,
    updateDate,
    deleteDate,
    isDemoMode
  };
};

export default useDemoDates;
